import axios, { AxiosError } from 'axios';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { InicioNexo, KitNexo, Material, MaterialResumo, PermissoesNexo } from './types';

const endpoint = `${API_URL}/admin/nexo`;

async function authConfig() {
  return { headers: { Authorization: await getToken() }, timeout: 30_000 };
}

interface AcessoNexo { permitido: boolean; admin: boolean; permissoes: PermissoesNexo }

function exigirPermissoes(valor: unknown): PermissoesNexo {
  const permissoes = valor as Partial<PermissoesNexo> | null | undefined;
  const chaves: Array<keyof PermissoesNexo> = ['consultar', 'editar_acervo', 'homologar', 'gerenciar_colaboradores'];
  if (!permissoes || chaves.some(chave => typeof permissoes[chave] !== 'boolean')) {
    throw new Error('As permissões do NEXO não foram informadas. Atualize a página ou procure o responsável pelo sistema.');
  }
  return permissoes as PermissoesNexo;
}

export async function obterAcesso(): Promise<AcessoNexo> {
  const resposta = await axios.get(endpoint, { ...(await authConfig()), params: { action: 'acesso' } });
  return { ...resposta.data, permissoes: exigirPermissoes(resposta.data?.permissoes) };
}

export async function obterInicio(): Promise<InicioNexo> {
  const resposta = await axios.get(endpoint, { ...(await authConfig()), params: { action: 'inicio' } });
  return { ...resposta.data, permissoes: exigirPermissoes(resposta.data?.permissoes) };
}

export async function listarMateriais(): Promise<MaterialResumo[]> {
  const resposta = await axios.get(endpoint, { ...(await authConfig()), params: { action: 'materiais' } });
  return resposta.data.materiais || [];
}

export async function obterMaterial(id: string): Promise<Material> {
  const resposta = await axios.get(endpoint, { ...(await authConfig()), params: { action: 'material', id } });
  return resposta.data;
}

export async function postarNexo(payload: Record<string, unknown>): Promise<void> {
  await axios.post(endpoint, payload, await authConfig());
}

function caminhoSeguro(caminho: string): string | null {
  if (!caminho || caminho.includes('\\') || caminho.startsWith('/') || /^[a-zA-Z]:/.test(caminho)) return null;
  const partes = caminho.split('/');
  if (partes.some(parte => !parte || parte === '.' || parte === '..')) return null;
  return partes.join('/');
}

export async function baixarKit(): Promise<string> {
  const resposta = await axios.get<KitNexo>(endpoint, { ...(await authConfig()), params: { action: 'kit' } });
  const kit = resposta.data;
  const zip = new JSZip();
  const vistos = new Set<string>();

  for (const arquivo of kit.arquivos || []) {
    const caminho = caminhoSeguro(arquivo.caminho);
    if (!caminho || vistos.has(caminho)) throw new Error(`O kit contém um caminho de arquivo inválido: ${arquivo.caminho}`);
    vistos.add(caminho);
    zip.file(caminho, arquivo.conteudo);
  }
  if (!vistos.size) throw new Error('O kit recebido está vazio.');

  const nomeBase = (kit.nome || 'nexo-kit').replace(/\.zip$/i, '').replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'nexo-kit';
  const revisaoCurta = String(kit.revisao || 'atual').replace(/[^a-zA-Z0-9_-]+/g, '').slice(0, 8) || 'atual';
  const blob = await zip.generateAsync({ type: 'blob' });
  saveAs(blob, `${nomeBase}-r${revisaoCurta}.zip`);
  return `${vistos.size} arquivo(s)`;
}

export function mensagemErro(erro: unknown): string {
  const axiosErro = erro as AxiosError<{ error?: string }>;
  return axiosErro.response?.data?.error || (erro instanceof Error ? erro.message : 'Não foi possível concluir a operação.');
}

export function conflitoDeRevisao(erro: unknown): boolean {
  return (erro as AxiosError)?.response?.status === 409;
}
